import React, { useState, useEffect, useMemo, useRef, startTransition } from "react";
import { MomentumScore } from "@/api/entities";
import { PersonaActivity } from "@/api/entities";
import { AccountFinal } from "@/api/entities";
import { PersonFinal } from "@/api/entities";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useSearchParams } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Building2, User as UserIcon, Calendar, Info, Globe } from "lucide-react";
import { formatDistanceToNow, isValid } from "date-fns";
import { es as dateFnsEs, ptBR as dateFnsPtBR, fr as dateFnsFr } from "date-fns/locale";
import { sanitizeId } from "@/components/utils/id";
import { Skeleton } from "@/components/ui/skeleton";
import { applyTenantOverrides } from '@/components/utils/tenantOverrides';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from '@/components/utils/translations';
import { resolveEffectiveTenant, isEvasysFamilyTenant, tenantIdsEqual } from '@/lib/tenantUtils';
import { fetchMarketUpdatesSafe } from '@/lib/competitorsApi';
import { formatUpdateType } from '@/lib/competitorDisplay';
import {
  accountCompetitorSignalTypeOptions,
  personaCompetitorSignalTypeOptions,
  signalTypeLabel,
} from '@/lib/competitorSignals';
import { displayPersonaScore, isPersonaReportingScore, PERSONA_REPORTING_FLOOR } from '@/lib/personaSignalScore';
import SignalsFeedCard, { formatSignalTypeLabel } from '@/components/signals/SignalsFeedCard';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';

const SIGNALS_CACHE_KEY = 'signals-cache-v9';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const DEFAULT_TENANT = 'instructure';

const sanitizeMomentumChange = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/–/g, '-').trim();
    if (!cleaned) return null;
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

/**
 * TWO LANES — same split as MomentumSummary / account signals:
 * - Legacy per-tenant rows carry `momentum_score_change` (weekly delta) → use that.
 * - Institutional rows have `momentum_score_change` null and carry `signal_score` → use that.
 */
const displayMomentumScore = (row) => {
  const legacyDelta = sanitizeMomentumChange(row?.momentum_score_change);
  if (legacyDelta != null) return legacyDelta;

  if (row?.signal_score != null && !Number.isNaN(Number(row.signal_score))) {
    return Number(row.signal_score);
  }
  return null;
};

const matchesCompetitorName = (record, competitorName) => {
  if (!competitorName?.trim()) return true;
  const needle = competitorName.trim().toLowerCase();
  const haystack = [
    record.competitor_name,
    record.summary,
    record.reason,
    record.institution_name,
    record.signal_type,
    record.title,
  ]
    .map((v) => String(v || '').toLowerCase())
    .join(' ');
  return haystack.includes(needle);
};

const matchesMarketUpdate = (record, competitorName) => {
  if (!competitorName?.trim()) return true;
  const needle = competitorName.trim().toLowerCase();
  const haystack = [
    record.competitor_name,
    record.competitor_group,
    record.evidence_summary,
    record.title,
    record.organization_name,
  ]
    .map((v) => String(v || '').toLowerCase())
    .join(' ');
  return haystack.includes(needle);
};

function resolveSignalsTab(searchParams) {
  const tab = searchParams.get('tab');
  if (tab === 'market') return 'market';
  if (tab === 'personas') return 'personas';
  if (tab === 'momentum') return 'momentum';
  if (searchParams.get('signal_focus') === 'market') return 'market';
  return 'market';
}

/** date-fns locale for the UI language; undefined (= date-fns default, English) for en. */
const DATE_FNS_LOCALES = { es: dateFnsEs, 'pt-BR': dateFnsPtBR, fr: dateFnsFr };

/**
 * Display-only translation keys, keyed on the RAW signal_type / update_type. English values
 * equal what formatSignalTypeLabel / signalTypeLabel / formatUpdateType render today; an
 * unknown type falls back to those helpers unchanged. Filter values, comparisons and search
 * still use the raw type. (Competitor + market-update maps mirror components/competitors/competitorUi.jsx.)
 */
const SIGNAL_TYPE_KEYS = {
  funding_news: 'sigTypeFundingNews',
  leadership_change: 'sigTypeLeadershipChange',
  strategic_announcement: 'sigTypeStrategicAnnouncement',
  partnership: 'sigTypePartnership',
  technology_adoption: 'sigTypeTechnologyAdoption',
  procurement_update: 'sigTypeProcurementUpdate',
  acquisition: 'sigTypeAcquisition',
  contract_award: 'sigTypeContractAward',
  profile_update: 'sigTypeProfileUpdate',
  role_change: 'sigTypeRoleChange',
  achievement: 'sigTypeAchievement',
  social_activity: 'sigTypeSocialActivity',
  incumbent: 'sigTypeIncumbent',
  buying: 'sigTypeBuying',
  displacement: 'sigTypeDisplacement',
  renewal_window: 'sigTypeRenewalWindow',
  budget_allocation: 'sigTypeBudgetAllocation',
  initiative: 'sigTypeInitiative',
  risk: 'sigTypeRisk',
  org_change: 'sigTypeOrgChange',
  accreditor_result: 'sigTypeAccreditorResult',
  grant_award: 'sigTypeGrantAward',
  job_posting: 'sigTypeJobPosting',
  estate_event: 'sigTypeEstateEvent',
  financial_pressure: 'sigTypeFinancialPressure',
  enrolment_pressure: 'sigTypeEnrolmentPressure',
  system_shortcoming: 'sigTypeSystemShortcoming',
  vendor_dispute: 'sigTypeVendorDispute',
  compliance_update: 'sigTypeComplianceUpdate',
  board_update: 'sigTypeBoardUpdate',
  greenfield: 'sigTypeGreenfield',
  engagement: 'engagement',
  product_release: 'sigUpdProductRelease',
};

const COMPETITOR_SIGNAL_TYPE_KEYS = {
  competitor_account_reference: 'acctCompSigAccountReference',
  competitor_account_adoption: 'acctCompSigAccountAdoption',
  competitor_partner_ecosystem: 'acctCompSigPartnerEcosystem',
  competitor_procurement_signal: 'acctCompSigProcurementSignal',
  competitor_event_overlap: 'acctCompSigEventOverlap',
  competitor_displacement_opportunity: 'acctCompSigDisplacementOpportunity',
  competitor_weakness_signal: 'acctCompSigWeaknessSignal',
  competitor_persona_interaction: 'acctCompSigPersonaInteraction',
  competitor_persona_reference: 'acctCompSigPersonaReference',
  competitor_persona_adoption: 'acctCompSigPersonaAdoption',
};

const MARKET_UPDATE_TYPE_KEYS = {
  ecosystem_signal: 'sigUpdEcosystemSignal',
  market_event: 'sigUpdMarketEvent',
  regional_event: 'sigUpdRegionalEvent',
  product_release: 'sigUpdProductRelease',
  product_launch: 'sigUpdProductLaunch',
  press_release: 'sigUpdPressRelease',
  partnership_announcement: 'sigUpdPartnershipAnnouncement',
  investor_news: 'sigUpdInvestorNews',
  webinar_event: 'sigUpdWebinarEvent',
  vendor_metric_claim: 'sigUpdVendorMetricClaim',
  external_account_adoption: 'sigUpdExternalAccountAdoption',
  external_reference_account: 'sigUpdExternalReferenceAccount',
  market_commentary: 'sigUpdMarketCommentary',
  conference_recap: 'sigUpdConferenceRecap',
};

function competitorSignalTypeDisplay(signalType, t) {
  const key = COMPETITOR_SIGNAL_TYPE_KEYS[signalType];
  if (key) return t(key);
  const fallback = signalTypeLabel(signalType);
  return fallback === 'Competitor signal' ? t('acctCompSigGeneric') : fallback;
}

function signalTypeDisplay(signalType, t) {
  const key = SIGNAL_TYPE_KEYS[signalType];
  return key ? t(key) : formatSignalTypeLabel(signalType);
}

function marketUpdateTypeDisplay(type, t) {
  const key = MARKET_UPDATE_TYPE_KEYS[type];
  return key ? t(key) : formatUpdateType(type);
}

function signalTypeBadge(signalType, t) {
  if (!signalType) return null;
  const label = signalType.startsWith('competitor_')
    ? competitorSignalTypeDisplay(signalType, t)
    : signalTypeDisplay(signalType, t);
  if (!label) return null;
  return <Badge variant="outline" className="text-xs">{label}</Badge>;
}

const safeFormatDate = (dateValue, t, locale) => {
  if (!dateValue) return t('recently');
  try {
    const date = new Date(dateValue);
    if (!isValid(date)) return t('recently');
    return formatDistanceToNow(date, { addSuffix: true, locale });
  } catch (e) {
    return t('recently');
  }
};

function readSignalsCache() {
  try {
    const raw = sessionStorage.getItem(SIGNALS_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.cachedAt > CACHE_DURATION) {
      sessionStorage.removeItem(SIGNALS_CACHE_KEY);
      return null;
    }
    return data;
  } catch { return null; }
}

function writeSignalsCache(snapshot) {
  try {
    sessionStorage.setItem(
      SIGNALS_CACHE_KEY,
      JSON.stringify({ ...snapshot, cachedAt: Date.now() })
    );
  } catch {}
}

export default function Signals({ currentTenant: currentTenantProp }) {
  const { t, language } = useTranslation();
  const dateLocale = DATE_FNS_LOCALES[language];
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const [searchParams] = useSearchParams();
  const [momentumScores, setMomentumScores] = useState([]);
  const [personaActivities, setPersonaActivities] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [people, setPeople] = useState([]);
  const [activeTab, setActiveTab] = useState(() => resolveSignalsTab(searchParams));
  const [selectedSignalType, setSelectedSignalType] = useState("all");
  const [minScoreChange, setMinScoreChange] = useState("");
  const [timeFilter, setTimeFilter] = useState("all");
  const [signalSearchTerm, setSignalSearchTerm] = useState("");
  const [marketUpdates, setMarketUpdates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hydratedFromCache, setHydratedFromCache] = useState(false);
  const [competitorFilterName, setCompetitorFilterName] = useState(
    () => searchParams.get('competitor') || '',
  );

  const aliveRef = useRef(true);
  const loadAttemptedRef = useRef(false);
  const dataLoadedRef = useRef(false);
  const lastLoadedTenantRef = useRef(null);

  const effectiveTenant = useMemo(() => resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    DEFAULT_TENANT
  ), [currentTenant, contextUser]);

  useEffect(() => {
    setActiveTab(resolveSignalsTab(searchParams));
    setCompetitorFilterName(searchParams.get('competitor') || '');
  }, [searchParams]);

  useEffect(() => {
    setSelectedSignalType('all');
  }, [activeTab]);

// Reset when tenant changes
useEffect(() => {
  if (lastLoadedTenantRef.current && lastLoadedTenantRef.current !== effectiveTenant) {
    console.log('🔄 Signals: Tenant changed from', lastLoadedTenantRef.current, 'to', effectiveTenant);
    dataLoadedRef.current = false;
    loadAttemptedRef.current = false;
    sessionStorage.removeItem(SIGNALS_CACHE_KEY);
  }
}, [effectiveTenant]);

useEffect(() => {
  if (dataLoadedRef.current) return;

  const cached = readSignalsCache();
  if (cached) {
    if (cached.tenantId && cached.tenantId !== effectiveTenant && effectiveTenant !== DEFAULT_TENANT) {
      console.log('📦 Signals: Cache is for different tenant, clearing...');
      sessionStorage.removeItem(SIGNALS_CACHE_KEY);
      return;
    }
    if (cached.signalFocus) {
      sessionStorage.removeItem(SIGNALS_CACHE_KEY);
      return;
    }
    
    // Build tenantMatches function to filter cached data
    const tenantMatches = (record) => {
      if (!record) return false;
      if (effectiveTenant === 'smowl') return record.tenant_id === 'smowl';
      if (record.tenant_id === effectiveTenant || record.tenant_id === 'global') return true;
      if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(effectiveTenant)) return true;
      if (record.tenant_overrides && record.tenant_overrides[effectiveTenant]) return true;
      if (effectiveTenant === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id || record.tenant_id === 'clairelabs')) return true;
      // Diambra should NOT see smowl signals
      if (effectiveTenant === 'diambra') {
        return record.tenant_id === 'diambra' || record.tenant_id === 'global';
      }
      if (effectiveTenant === 'edqwest') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
      if (effectiveTenant === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
      if (effectiveTenant === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
          if (effectiveTenant === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise' || record.tenant_id === 'global';
          if (effectiveTenant === 'kairadigital') return record.tenant_id === 'kairadigital';
          if (isEvasysFamilyTenant(effectiveTenant)) return tenantIdsEqual(record.tenant_id, effectiveTenant) || record.tenant_id === 'global';
          if (effectiveTenant === 'learnwise') return record.tenant_id === 'learnwise' || record.tenant_id === 'global';
          return false;
        };
    
    const signalTenantMatches = (record) => {
      // `global_temp` is the shared institutional signal layer. The backend only
      // returns it to tenants entitled to it, so if a row arrives it is meant to be
      // seen; re-filtering it out here just discards what was correctly served.
      if (record?.tenant_id === 'global_temp') return true;
      if (effectiveTenant === 'diambra') {
        return record.tenant_id === 'diambra' || record.tenant_id === 'global';
      }
      if (effectiveTenant === 'calmedica') return record.tenant_id === 'calmedica';
      if (effectiveTenant === 'edqwest') return record.tenant_id === 'edqwest';
      if (effectiveTenant === 'faria') return record.tenant_id === 'edqwest';
      if (effectiveTenant === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
      if (effectiveTenant === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise';
      if (effectiveTenant === 'kairadigital') return record.tenant_id === 'kairadigital';
      if (isEvasysFamilyTenant(effectiveTenant)) return tenantIdsEqual(record.tenant_id, effectiveTenant) || record.tenant_id === 'global';
      if (effectiveTenant === 'learnwise') return record.tenant_id === 'learnwise' || record.tenant_id === 'global';
      return tenantMatches(record);
    };
    
    // Filter cached signals to ensure tenant isolation
    const filteredMomentum = (cached.momentumScores || []).filter(signalTenantMatches);
    const filteredActivities = (cached.personaActivities || []).filter(signalTenantMatches);
    const filteredAccounts = (cached.accounts || []).filter(tenantMatches);
    const filteredPeople = (cached.people || []).filter(tenantMatches);
    
    if (filteredMomentum.length !== (cached.momentumScores || []).length || 
        filteredActivities.length !== (cached.personaActivities || []).length) {
      console.warn('⚠️ Signals: Filtered out cached signals that don\'t match tenant:', effectiveTenant, {
        momentum: (cached.momentumScores || []).length - filteredMomentum.length,
        activities: (cached.personaActivities || []).length - filteredActivities.length
      });
    }
    
    console.log('📦 Signals: Hydrating from cache for tenant:', effectiveTenant);
    setMomentumScores(filteredMomentum);
    setPersonaActivities(filteredActivities);
    setAccounts(filteredAccounts);
    setPeople(filteredPeople);
    setMarketUpdates(cached.marketUpdates || []);
    setHydratedFromCache(true);
    setIsLoading(false);
    dataLoadedRef.current = true;
  }
}, [effectiveTenant]);

  useEffect(() => {
    const loadData = async () => {
      if (!contextUser) return;
      if (loadAttemptedRef.current) {
        console.log('⏭️ Signals: Load already in progress, skipping');
        return;
      }

      if (dataLoadedRef.current &&
          lastLoadedTenantRef.current === effectiveTenant) {
        console.log('✅ Signals: Data already loaded for current tenant, skipping');
        return;
      }

      console.log('🔄 Signals page: Starting data load for tenant:', effectiveTenant);
      loadAttemptedRef.current = true;

      if (!hydratedFromCache && momentumScores.length === 0) {
        setIsLoading(true);
      }

      try {
        console.log('🔄 Fetching signals data...');
        
        const tenantMatches = (record) => {
          if (!record) return false;
          if (effectiveTenant === 'smowl') return record.tenant_id === 'smowl';
          if (record.tenant_id === effectiveTenant || record.tenant_id === 'global') {
            return true;
          }
          
          if (effectiveTenant === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id)) {
            return true;
          }
          if (effectiveTenant === 'instructure' && record.tenant_id === 'clairelabs') {
            return true;
          }
          if (effectiveTenant === 'diambra' && 
              (record.tenant_id === 'smowl' || record.tenant_id === 'instructure' || record.tenant_id === 'clairelabs' || record.tenant_id === 'base44' || record.tenant_id === 'default')) {
            return true;
          }
          if (effectiveTenant === 'edqwest') return record.tenant_id === 'edqwest';
          if (effectiveTenant === 'faria') return record.tenant_id === 'edqwest';
          if (effectiveTenant === 'calmedica') return record.tenant_id === 'calmedica';
          if (effectiveTenant === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
          if (effectiveTenant === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise' || record.tenant_id === 'global';
          if (effectiveTenant === 'kairadigital') return record.tenant_id === 'kairadigital';

          if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(effectiveTenant)) {
            return true;
          }
          
          if (record.tenant_overrides && record.tenant_overrides[effectiveTenant]) {
            return true;
          }
          
          return false;
        };

        // Build tenant filter for API calls (same logic as Market.jsx)
        let tenantFilter;
        if (effectiveTenant === 'instructure') {
          tenantFilter = { $in: ['instructure', 'clairelabs', 'default', 'global'] };
        } else if (effectiveTenant === 'smowl') {
          tenantFilter = { $in: ['smowl'] };
        } else if (effectiveTenant === 'diambra') {
          // Diambra should ONLY see diambra signals, NOT smowl or other tenants
          tenantFilter = { $in: ['diambra', 'default', 'global'] };
        } else if (effectiveTenant === 'clairelabs') {
          tenantFilter = { $in: ['clairelabs', 'default', 'global'] };
        } else if (effectiveTenant === 'faria' || effectiveTenant === 'edqwest') {
          tenantFilter = { $in: ['edqwest', 'default', 'global'] };
        } else if (effectiveTenant === 'calmedica') {
          tenantFilter = { $in: [effectiveTenant] };
        } else if (effectiveTenant === 'sebit') {
          tenantFilter = { $in: [effectiveTenant, 'global'] };
        } else if (effectiveTenant === 'plataformaa') {
          tenantFilter = { $in: [effectiveTenant, 'global'] };
        } else if (effectiveTenant === 'skillwell') {
          tenantFilter = { $in: ['skillwell', 'skillwise', 'global'] };
        } else if (effectiveTenant === 'kairadigital') {
          tenantFilter = { $in: [effectiveTenant] };
        } else if (isEvasysFamilyTenant(effectiveTenant)) {
          // Evasys / evasys_V2: strict isolation — only this tenant and global
          tenantFilter = { $in: [effectiveTenant, 'global'] };
        } else if (effectiveTenant === 'learnwise') {
          // Learnwise: strict isolation — only learnwise and global, no default/other tenants
          tenantFilter = { $in: ['learnwise', 'global'] };
        } else {
          tenantFilter = { $in: [effectiveTenant, 'default', 'global'] };
        }

        // FIXED: Strict tenant matching for signals - no cross-tenant visibility
        const signalTenantMatches = (record) => {
          // `global_temp` is the shared institutional signal layer. The backend only
          // returns it to tenants entitled to it, so if a row arrives it is meant to be
          // seen; re-filtering it out here just discards what was correctly served.
          if (record?.tenant_id === 'global_temp') return true;

          // EdQwest and Faria both see only edqwest signals
          if (effectiveTenant === 'edqwest') {
            return record.tenant_id === 'edqwest';
          }
          if (effectiveTenant === 'faria') {
            return record.tenant_id === 'edqwest';
          }
          
          // Calmedica sees only calmedica signals
          if (effectiveTenant === 'calmedica') {
            return record.tenant_id === 'calmedica';
          }
          
          // Diambra sees ONLY diambra signals (NOT smowl or other tenants)
          if (effectiveTenant === 'diambra') {
            return record.tenant_id === 'diambra' || record.tenant_id === 'global';
          }
          
          // Sebit sees sebit and global signals
          if (effectiveTenant === 'sebit') {
            return record.tenant_id === 'sebit' || record.tenant_id === 'global';
          }
          
          // Skillwell sees only skillwell signals (strict matching, including typo variant)
          if (effectiveTenant === 'skillwell') {
            return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise';
          }
          
          // Kairadigital sees only kairadigital signals
          if (effectiveTenant === 'kairadigital') {
            return record.tenant_id === 'kairadigital';
          }

          // Evasys: strict isolation — only evasys and global
          if (isEvasysFamilyTenant(effectiveTenant)) {
            return tenantIdsEqual(record.tenant_id, effectiveTenant) || record.tenant_id === 'global';
          }
          
          // Learnwise: strict isolation — only learnwise and global
          if (effectiveTenant === 'learnwise') {
            return record.tenant_id === 'learnwise' || record.tenant_id === 'global';
          }
          
          // For other tenants, use standard matching
          return tenantMatches(record);
        };

        // The backend resolves which partitions this tenant may read, from its
        // entitlement. It only does so when the caller has NOT pinned tenant_id —
        // passing the column replaces that logic with a hand-maintained list, which is
        // how the V2 tenants ended up reading zero of their 62,325 signals.
        const [momentumData, activityData, accountData, peopleData, marketRows] = await Promise.all([
          MomentumScore.filter({}, '-date_published', 10000),
          PersonaActivity.filter({}, '-date_published', 10000),
          AccountFinal.filter({ tenant_id: tenantFilter }, '', 200),
          PersonFinal.filter({ tenant_id: tenantFilter }, '', 200),
          fetchMarketUpdatesSafe(
            effectiveTenant,
            competitorFilterName ? { competitor: competitorFilterName } : {},
          ),
        ]);

        if (!aliveRef.current) return;

        const filteredMomentum = momentumData
          .filter(m => signalTenantMatches(m) && m.is_visible !== false)
          .map(m => {
            const overridden = applyTenantOverrides(m, effectiveTenant);
            return {
              ...overridden,
              momentum_score_change: sanitizeMomentumChange(overridden.momentum_score_change),
              signal_score:
                overridden.signal_score != null && !Number.isNaN(Number(overridden.signal_score))
                  ? Number(overridden.signal_score)
                  : null,
            };
          });

        const filteredActivities = activityData
          .filter(a => signalTenantMatches(a) && a.is_visible !== false)
          .map(a => applyTenantOverrides(a, effectiveTenant));

        const filteredAccounts = accountData
          .filter(acc => tenantMatches(acc) && acc.is_visible !== false)
          .map(acc => applyTenantOverrides(acc, effectiveTenant));

        const filteredPeople = peopleData
          .filter(p => tenantMatches(p) && p.is_visible !== false)
          .map(p => applyTenantOverrides(p, effectiveTenant));

        startTransition(() => {
          if (!aliveRef.current) return;
          
          setMomentumScores(filteredMomentum);
          setPersonaActivities(filteredActivities);
          setAccounts(filteredAccounts);
          setPeople(filteredPeople);
          setMarketUpdates(marketRows);
          
          writeSignalsCache({
            momentumScores: filteredMomentum,
            personaActivities: filteredActivities,
            accounts: filteredAccounts,
            people: filteredPeople,
            marketUpdates: marketRows,
            tenantId: effectiveTenant,
            cachedAt: Date.now()
          });
          
          setIsLoading(false);
          setHydratedFromCache(true);
          dataLoadedRef.current = true;
          lastLoadedTenantRef.current = effectiveTenant;
          
          console.log('✅ Signals page data loaded for tenant:', effectiveTenant, {
            momentum: filteredMomentum.length,
            activities: filteredActivities.length
          });
        });

      } catch (error) {
        if (!aliveRef.current) return;
        console.error("❌ Error loading signals data:", error);
        setIsLoading(false);
      } finally {
        loadAttemptedRef.current = false;
      }
    };

    aliveRef.current = true;
    loadData();
    
    return () => {
      aliveRef.current = false;
    };
  }, [effectiveTenant, hydratedFromCache, contextUser, competitorFilterName]);

  const filteredMomentumScores = useMemo(() => {
    let filtered = momentumScores;
    const q = signalSearchTerm.trim().toLowerCase();

    if (competitorFilterName) {
      filtered = filtered.filter((score) => matchesCompetitorName(score, competitorFilterName));
    }
    
    if (selectedSignalType !== "all") {
      filtered = filtered.filter(score => score.signal_type === selectedSignalType);
    }
    
    if (minScoreChange !== "") {
      const threshold = parseFloat(minScoreChange);
      if (!isNaN(threshold)) {
        if (threshold < 0) {
          filtered = filtered.filter((score) => {
            const value = displayMomentumScore(score);
            return value != null && value <= threshold;
          });
        } else {
          filtered = filtered.filter((score) => {
            const value = displayMomentumScore(score);
            return value != null && value >= threshold;
          });
        }
      }
    }
    
    if (timeFilter !== "all") {
      const now = new Date();
      filtered = filtered.filter(score => {
        const dateStr = score.date_published || score.weekly_period;
        if (!dateStr) return false;
        
        try {
          const scoreDate = new Date(dateStr);
          if (!isValid(scoreDate)) return false;
          
          const diffDays = Math.floor((now - scoreDate) / (1000 * 60 * 60 * 24));
          
          if (timeFilter === "7d") return diffDays <= 7;
          if (timeFilter === "30d") return diffDays <= 30;
          if (timeFilter === "90d") return diffDays <= 90;
          return true;
        } catch (e) {
          return false;
        }
      });
    }
    if (q) {
      filtered = filtered.filter((score) => {
        const haystack = [
          score.summary,
          score.signal_type,
          score.institution_name,
          score.reason,
        ]
          .map((v) => String(v || '').toLowerCase())
          .join(' ');
        return haystack.includes(q);
      });
    }

    return [...filtered].sort((a, b) => {
      const aDate = new Date(a.date_published || a.weekly_period || 0).getTime();
      const bDate = new Date(b.date_published || b.weekly_period || 0).getTime();
      return bDate - aDate;
    });
  }, [momentumScores, selectedSignalType, minScoreChange, timeFilter, signalSearchTerm, competitorFilterName]);

  const filteredPersonaActivities = useMemo(() => {
    let filtered = personaActivities;
    const q = signalSearchTerm.trim().toLowerCase();

    if (competitorFilterName) {
      filtered = filtered.filter((activity) => matchesCompetitorName(activity, competitorFilterName));
    }
    
    if (selectedSignalType !== "all") {
      filtered = filtered.filter(activity => activity.signal_type === selectedSignalType);
    }
    
    if (minScoreChange !== "") {
      const threshold = Math.abs(parseFloat(minScoreChange));
      if (!isNaN(threshold)) {
        filtered = filtered.filter((activity) => {
          const score = displayPersonaScore(activity);
          return score != null && Math.abs(score) >= threshold;
        });
      }
    } else {
      filtered = filtered.filter((activity) => isPersonaReportingScore(displayPersonaScore(activity)));
    }
    
    if (timeFilter !== "all") {
      const now = new Date();
      filtered = filtered.filter(activity => {
        if (!activity.date_published) return false;
        
        try {
          const activityDate = new Date(activity.date_published);
          if (!isValid(activityDate)) return false;
          
          const diffDays = Math.floor((now - activityDate) / (1000 * 60 * 60 * 24));
          
          if (timeFilter === "7d") return diffDays <= 7;
          if (timeFilter === "30d") return diffDays <= 30;
          if (timeFilter === "90d") return diffDays <= 90;
          return true;
        } catch (e) {
          return false;
        }
      });
    }
    if (q) {
      filtered = filtered.filter((activity) => {
        const haystack = [
          activity.summary,
          activity.signal_type,
          activity.account_name,
          activity.full_name,
          activity.first_name,
          activity.last_name,
        ]
          .map((v) => String(v || '').toLowerCase())
          .join(' ');
        return haystack.includes(q);
      });
    }

    return [...filtered].sort((a, b) => {
      const scoreA = displayPersonaScore(a) ?? Number.NEGATIVE_INFINITY;
      const scoreB = displayPersonaScore(b) ?? Number.NEGATIVE_INFINITY;
      if (scoreB !== scoreA) return scoreB - scoreA;
      const aDate = new Date(a.date_published || 0).getTime();
      const bDate = new Date(b.date_published || 0).getTime();
      return bDate - aDate;
    });
  }, [personaActivities, selectedSignalType, minScoreChange, timeFilter, signalSearchTerm, competitorFilterName]);

  const filteredMarketUpdates = useMemo(() => {
    let filtered = marketUpdates;
    const q = signalSearchTerm.trim().toLowerCase();

    if (competitorFilterName) {
      filtered = filtered.filter((row) => matchesMarketUpdate(row, competitorFilterName));
    }

    if (selectedSignalType !== 'all') {
      filtered = filtered.filter((row) => row.update_type === selectedSignalType);
    }

    if (timeFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter((row) => {
        if (!row.date_published) return false;
        try {
          const rowDate = new Date(row.date_published);
          if (!isValid(rowDate)) return false;
          const diffDays = Math.floor((now - rowDate) / (1000 * 60 * 60 * 24));
          if (timeFilter === '7d') return diffDays <= 7;
          if (timeFilter === '30d') return diffDays <= 30;
          if (timeFilter === '90d') return diffDays <= 90;
          return true;
        } catch {
          return false;
        }
      });
    }

    if (q) {
      filtered = filtered.filter((row) => {
        const haystack = [
          row.title,
          row.evidence_summary,
          row.competitor_name,
          row.organization_name,
          row.update_type,
        ]
          .map((v) => String(v || '').toLowerCase())
          .join(' ');
        return haystack.includes(q);
      });
    }

    return [...filtered].sort((a, b) => {
      const aDate = new Date(a.date_published || 0).getTime();
      const bDate = new Date(b.date_published || 0).getTime();
      return bDate - aDate;
    });
  }, [marketUpdates, selectedSignalType, timeFilter, signalSearchTerm, competitorFilterName]);

  const marketUpdateTypeOptions = useMemo(() => {
    const types = new Set(
      marketUpdates.map((row) => row.update_type).filter(Boolean),
    );
    return [...types]
      .sort()
      .map((value) => ({ value, label: formatUpdateType(value) }));
  }, [marketUpdates]);

  const accountCompetitorTypes = useMemo(() => accountCompetitorSignalTypeOptions(), []);
  const personaCompetitorTypes = useMemo(() => personaCompetitorSignalTypeOptions(), []);

  const showSkeletons = isLoading && !hydratedFromCache;
  const isMarketTab = activeTab === 'market';
  const showMinScore = !isMarketTab;

  const renderMarketFeedItem = (event) => {
    const title = event.evidence_summary || event.title || t('sigMarketUpdate');
    const meta = [
      ...(event.competitor_name
        ? [{ icon: Building2, label: event.competitor_name }]
        : []),
      ...(event.organization_name
        ? [{ icon: Globe, label: event.organization_name }]
        : []),
      {
        icon: Calendar,
        label: safeFormatDate(event.date_published, t, dateLocale),
      },
    ];
    const badges = [
      event.update_type ? (
        <Badge key="type" variant="outline" className="text-xs">
          {marketUpdateTypeDisplay(event.update_type, t)}
        </Badge>
      ) : null,
    ].filter(Boolean);

    return (
      <SignalsFeedCard
        key={event.update_id || event.id}
        Icon={Globe}
        iconWrapClass="bg-gray-100"
        iconClass="text-gray-600"
        title={title}
        sourceUrl={event.source_url}
        linkAccent="decoration-gray-400 hover:text-gray-700"
        meta={meta}
        badges={badges}
      />
    );
  };

  const renderAccountFeedItem = (score) => {
    const account = accounts.find(
      (a) =>
        a.id === score.account_id ||
        a.account_id === score.account_id ||
        a.account_name === score.institution_name,
    );
    const displayScore = displayMomentumScore(score);
    const isPositive = (displayScore ?? 0) >= 0;
    const isCalmedicaTenant = effectiveTenant === 'calmedica';
    const meta = [
      {
        icon: Building2,
        label: score.institution_name || '—',
        href: createPageUrl(
          `AccountFinal?id=${sanitizeId(account?.id || score.account_id || score.institution_name)}`,
        ),
      },
      {
        icon: Calendar,
        label: safeFormatDate(score.date_published || score.weekly_period, t, dateLocale),
      },
    ];
    const badges = [
      signalTypeBadge(score.signal_type, t),
      isCalmedicaTenant && score.department ? (
        <Badge key="dept" className="bg-purple-100 text-purple-700 text-xs">
          {score.department}
        </Badge>
      ) : null,
    ].filter(Boolean);

    return (
      <SignalsFeedCard
        key={score.id}
        Icon={isPositive ? TrendingUp : TrendingDown}
        iconWrapClass={isPositive ? 'bg-green-100' : 'bg-red-100'}
        iconClass={isPositive ? 'text-green-600' : 'text-red-600'}
        title={score.summary}
        sourceUrl={score.source_url}
        score={displayScore}
        meta={meta}
        badges={badges}
      />
    );
  };

  const renderPersonaFeedItem = (activity) => {
    const person = people.find(
      (p) =>
        p.id === activity.person_id ||
        p.person_id === activity.person_id ||
        (p.first_name === activity.first_name && p.last_name === activity.last_name),
    );
    const personIdForLink = sanitizeId(person?.id || activity.person_id);
    const personaScore = displayPersonaScore(activity);
    const isPositive = (personaScore ?? 0) >= 0;
    const externalUrl = activity.news_source_url || activity.full_url || activity.source_url;
    const meta = [
      {
        icon: UserIcon,
        label: activity.full_name || `${activity.first_name || ''} ${activity.last_name || ''}`.trim(),
        href: createPageUrl(`PersonProfile?id=${encodeURIComponent(personIdForLink)}`),
      },
      ...(activity.account_name
        ? [{ icon: Building2, label: activity.account_name }]
        : []),
      {
        icon: Calendar,
        label: safeFormatDate(activity.date_published, t, dateLocale),
      },
    ];
    const badges = [signalTypeBadge(activity.signal_type, t)].filter(Boolean);

    return (
      <SignalsFeedCard
        key={activity.id}
        Icon={UserIcon}
        iconWrapClass={isPositive ? 'bg-purple-100' : 'bg-amber-100'}
        iconClass={isPositive ? 'text-purple-600' : 'text-amber-700'}
        title={activity.summary}
        sourceUrl={externalUrl}
        linkAccent="decoration-purple-500 hover:text-purple-600"
        score={personaScore}
        signedDecimal
        scoreWarning={!isPositive}
        meta={meta}
        badges={badges}
      />
    );
  };

  return (
    <div className="bg-gray-50 -m-8 -mt-8 min-h-screen">
      <div className="bg-white border-b mb-6 -mx-8">
        <div className="max-w-7xl mx-auto px-12 sm:px-8 md:px-10 py-8">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-[#0A1A2F]">{t('signals')}</h1>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="p-1 rounded-full hover:bg-gray-100 transition-colors">
                    <Info className="w-5 h-5 text-[#5C6B7A]" />
                  </button>
                </TooltipTrigger>
                <TooltipContent 
                  side="bottom"
                  align="start"
                  className="w-[90vw] max-w-[700px] bg-[#0A1A2F] text-white border-0 shadow-lg p-6 max-h-[80vh] overflow-y-auto"
                >
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-bold text-[#00C2C2] mb-2">Momentum Score (Account Level)</h3>
                      <p className="text-sm leading-relaxed mb-3">
                        The Momentum Score reflects weekly organizational developments that make the environment more or less favourable for the client&apos;s engagement, expansion, or renewal.
                      </p>
                      <p className="text-sm font-semibold text-[#00C2C2] mb-1">Signals Captured:</p>
                      <ul className="text-sm space-y-1 list-disc list-inside ml-2 mb-3">
                        <li>Launches or expansions of digital or innovation projects</li>
                        <li>Funding, strategy, or policy updates in relevant domains</li>
                        <li>Leadership or governance changes influencing digital direction</li>
                        <li>Procurement awards, tenders, or contract expiries</li>
                        <li>Service-quality, compliance, or reputation events</li>
                        <li>Competitor activity or renewals reducing near-term opportunity</li>
                      </ul>
                      <p className="text-sm font-semibold text-[#00C2C2] mb-1">Score Derivation:</p>
                      <ul className="text-sm space-y-1 ml-2">
                        <li><span className="text-green-400">+4 to +5:</span> Strongly favourable (new initiative, open procurement, leadership supportive of innovation)</li>
                        <li><span className="text-green-300">+1 to +3:</span> Moderately favourable (funding, pilot success)</li>
                        <li><span className="text-gray-400">0:</span> Informational only</li>
                        <li><span className="text-orange-300">−1 to −3:</span> Moderately unfavourable (project delay, budget constraint)</li>
                        <li><span className="text-red-400">−4 to −5:</span> Strongly unfavourable (competitor renewal, strategic shift away from client domain)</li>
                      </ul>
                      <p className="text-xs text-gray-400 mt-2 italic">
                        Scores are aggregated weekly by publication date and deduplicated to avoid counting overlapping items.
                      </p>
                    </div>
                    
                    <div className="border-t border-gray-700 pt-4">
                      <h3 className="text-lg font-bold text-purple-400 mb-2">Persona Activity Score (Individual Level)</h3>
                      <p className="text-sm leading-relaxed mb-3">
                        The Persona Activity Score tracks how visibly and substantively a contact contributes to digital, operational, or strategic change in areas relevant to the client&apos;s solution space.
                      </p>
                      <p className="text-sm font-semibold text-purple-400 mb-1">Signals Captured:</p>
                      <ul className="text-sm space-y-1 list-disc list-inside ml-2 mb-3">
                        <li>Leadership or authorship in tenders, pilots, or funded projects</li>
                        <li>Involvement in transformation, innovation, or service-improvement programmes</li>
                        <li>Publications, speaking engagements, or policy authorship in relevant domains</li>
                        <li>Leadership or role changes influencing digital or operational strategy</li>
                        <li>Engagement with competing vendors or technologies, signalling reduced alignment with the client</li>
                      </ul>
                      <p className="text-sm font-semibold text-purple-400 mb-1">Score Derivation:</p>
                      <p className="text-sm mb-2">Each verified signal adjusts the Persona Activity Score between +5 and −5 based on strength and relevance:</p>
                      <ul className="text-sm space-y-1 ml-2">
                        <li><span className="text-green-400">+4 to +5:</span> Direct ownership of a project or procurement relevant to the client</li>
                        <li><span className="text-green-300">+2 to +3:</span> Strategic or operational contribution</li>
                        <li><span className="text-gray-400">0 to +1:</span> Informational only (no vendor impact)</li>
                        <li><span className="text-red-400">−3 to −5:</span> Competitor-favouring or negative signal</li>
                      </ul>
                      <p className="text-xs text-gray-400 mt-2 italic">
                        Scores are deduplicated by source, supported by dated, verifiable evidence, and updated over time.
                      </p>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-lg text-[#5C6B7A]">{t('trackSignals')}</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-2 sm:px-6 py-0">
      <Card className="border-0 shadow-md">
        <CardContent className="p-4 md:p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="space-y-3 mb-4 md:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <TabsList className="bg-gray-100 w-fit">
                  <TabsTrigger value="market" className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {t('market')} ({filteredMarketUpdates.length})
                  </TabsTrigger>
                  <TabsTrigger value="momentum" className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {t('accounts')} ({filteredMomentumScores.length})
                  </TabsTrigger>
                  <TabsTrigger value="personas" className="flex items-center gap-2">
                    <UserIcon className="w-4 h-4" />
                    {t('personas')} ({filteredPersonaActivities.length})
                  </TabsTrigger>
                </TabsList>
                {competitorFilterName ? (
                  <Badge variant="outline" className="text-xs w-fit shrink-0">
                    {t('sigCompetitorFilter', { name: competitorFilterName })}
                  </Badge>
                ) : null}
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                <Input
                  type="text"
                  placeholder={`${t('search')}...`}
                  value={signalSearchTerm}
                  onChange={(e) => setSignalSearchTerm(e.target.value)}
                  className="w-full sm:flex-1 sm:min-w-[10rem] h-9"
                />
                <Select value={selectedSignalType} onValueChange={setSelectedSignalType}>
                  <SelectTrigger className="w-full sm:w-52 h-9">
                    <SelectValue placeholder={t('filterBySignal')} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="all">{t('allSignals')}</SelectItem>
                    {activeTab === 'market' ? (
                      <SelectGroup>
                        <SelectLabel>{t('sigMarketActivity')}</SelectLabel>
                        {marketUpdateTypeOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {marketUpdateTypeDisplay(opt.value, t)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ) : activeTab === 'momentum' ? (
                      <>
                        <SelectGroup>
                          <SelectLabel>{t('sigAccountSignals')}</SelectLabel>
                          <SelectItem value="funding_news">{signalTypeDisplay('funding_news', t)}</SelectItem>
                          <SelectItem value="leadership_change">{signalTypeDisplay('leadership_change', t)}</SelectItem>
                          <SelectItem value="strategic_announcement">{signalTypeDisplay('strategic_announcement', t)}</SelectItem>
                          <SelectItem value="partnership">{signalTypeDisplay('partnership', t)}</SelectItem>
                          <SelectItem value="technology_adoption">{signalTypeDisplay('technology_adoption', t)}</SelectItem>
                          <SelectItem value="procurement_update">{signalTypeDisplay('procurement_update', t)}</SelectItem>
                          <SelectItem value="acquisition">{signalTypeDisplay('acquisition', t)}</SelectItem>
                          <SelectItem value="contract_award">{signalTypeDisplay('contract_award', t)}</SelectItem>
                          <SelectItem value="product_release">{signalTypeDisplay('product_release', t)}</SelectItem>
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>{t('sigCompetitorSignals')}</SelectLabel>
                          {accountCompetitorTypes.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {competitorSignalTypeDisplay(opt.value, t)}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </>
                    ) : (
                      <>
                        <SelectGroup>
                          <SelectLabel>{t('sigPersonaSignals')}</SelectLabel>
                          <SelectItem value="profile_update">{signalTypeDisplay('profile_update', t)}</SelectItem>
                          <SelectItem value="role_change">{signalTypeDisplay('role_change', t)}</SelectItem>
                          <SelectItem value="engagement">{signalTypeDisplay('engagement', t)}</SelectItem>
                          <SelectItem value="achievement">{signalTypeDisplay('achievement', t)}</SelectItem>
                          <SelectItem value="social_activity">{signalTypeDisplay('social_activity', t)}</SelectItem>
                          <SelectItem value="procurement_update">{signalTypeDisplay('procurement_update', t)}</SelectItem>
                          <SelectItem value="funding_news">{signalTypeDisplay('funding_news', t)}</SelectItem>
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>{t('sigCompetitorSignals')}</SelectLabel>
                          {personaCompetitorTypes.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {competitorSignalTypeDisplay(opt.value, t)}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </>
                    )}
                  </SelectContent>
                </Select>
                <Select value={timeFilter} onValueChange={setTimeFilter}>
                  <SelectTrigger className="w-full sm:w-32 h-9">
                    <SelectValue placeholder={t('filter')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allTime')}</SelectItem>
                    <SelectItem value="7d">{t('last7Days')}</SelectItem>
                    <SelectItem value="30d">{t('last30Days')}</SelectItem>
                    <SelectItem value="90d">{t('last90Days')}</SelectItem>
                  </SelectContent>
                </Select>
                {showMinScore ? (
                  <Input
                    type="number"
                    placeholder={activeTab === 'personas' ? String(PERSONA_REPORTING_FLOOR) : t('minScore')}
                    value={minScoreChange}
                    onChange={(e) => setMinScoreChange(e.target.value)}
                    className="w-full sm:w-28 h-9"
                    step="0.1"
                  />
                ) : null}
              </div>
            </div>

            <TabsContent value="market" className="mt-0 space-y-3">
              {showSkeletons ? (
                <div className="space-y-3">
                  {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-24 w-full" />)}
                </div>
              ) : filteredMarketUpdates.length > 0 ? (
                <div className="space-y-3">
                  {filteredMarketUpdates.map((event) => renderMarketFeedItem(event))}
                </div>
              ) : (
                <div className="text-center py-12 text-[#5C6B7A]">
                  <Globe className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">{t('sigNoMarketActivity')}</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="momentum" className="mt-0 space-y-3">
              {showSkeletons ? (
                <div className="space-y-3">
                  {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-24 w-full" />)}
                </div>
              ) : filteredMomentumScores.length > 0 ? (
                <div className="space-y-3">
                  {filteredMomentumScores.map((score) => renderAccountFeedItem(score))}
                </div>
              ) : (
                <div className="text-center py-12 text-[#5C6B7A]">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">{t('noAccountSignals')}</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="personas" className="mt-0 space-y-3">
              {showSkeletons ? (
                <div className="space-y-3">
                  {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-24 w-full" />)}
                </div>
              ) : filteredPersonaActivities.length > 0 ? (
                <div className="space-y-3">
                  {filteredPersonaActivities.map((activity) => renderPersonaFeedItem(activity))}
                </div>
              ) : (
                <div className="text-center py-12 text-[#5C6B7A]">
                  <UserIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">{t('noPersonaSignals')}</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}